<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import CometNavbar from '@/components/CometNavbar.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';

defineOptions({ name: 'CheckinResultLayout' });

const props = withDefaults(
  defineProps<{
    variant: 'success' | 'warning' | 'error';
    icon?: string;
    title: string;
    message?: string;
    description?: string;
    warehouseId?: string | null;
    seconds?: number;
  }>(),
  {
    icon: undefined,
    message: undefined,
    description: undefined,
    warehouseId: null,
    seconds: 20
  }
);

const defaultIcons: Record<typeof props.variant, string> = {
  success: 'mdi-check',
  warning: 'mdi-exclamation',
  error: 'mdi-cancel'
};

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const secondsRemaining = ref(props.seconds);
let timer: ReturnType<typeof setInterval> | null = null;

function goToStart() {
  const lang = route.query.lang;
  router.push({
    name: 'initial',
    query: {
      warehouseId: props.warehouseId,
      ...(typeof lang === 'string' && lang ? { lang } : {})
    }
  });
}

onMounted(() => {
  if (!props.warehouseId) {
    return;
  }
  timer = setInterval(() => {
    secondsRemaining.value -= 1;
    if (secondsRemaining.value === 0) {
      if (timer) {
        clearInterval(timer);
      }
      goToStart();
    }
  }, 1000);
});

onUnmounted(() => {
  if (timer) {
    clearInterval(timer);
  }
});
</script>

<template>
  <div class="fill-height d-flex flex-column gap-6 pa-6 checkin-result">
    <comet-navbar primary>
      <language-selector />
    </comet-navbar>

    <div class="d-flex flex-column align-center text-center gap-3 comet-i18n-exclude">
      <div class="checkin-result__ring" :class="`checkin-result__ring--${variant}`">
        <div class="checkin-result__circle" :class="`checkin-result__circle--${variant}`">
          <v-icon size="32" color="white">{{ icon ?? defaultIcons[variant] }}</v-icon>
        </div>
      </div>

      <p class="typography-heading-md-bold" :class="`checkin-result__title--${variant}`">
        {{ title }}
      </p>
      <p v-if="message" class="typography-body-md-bold checkin-result__message--error">
        {{ message }}
      </p>
      <p v-if="description" class="typography-body-md text__color-text-tertiary">
        {{ description }}
      </p>
    </div>

    <div class="d-flex flex-column gap-4 flex-grow-1">
      <slot></slot>
    </div>

    <div v-if="warehouseId">
      <v-btn block color="primary" class="comet-i18n-exclude" @click="goToStart">
        {{ t('completed.finalize', { seconds: secondsRemaining }) }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.checkin-result__ring {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &--success {
    background: $color-success-20;
  }

  &--warning {
    background: $color-warning-20;
  }

  &--error {
    background: $color-error-20;
  }
}

.checkin-result__circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &--success {
    background: $color-success-60;
  }

  &--warning {
    background: $color-warning-40;
  }

  &--error {
    background: $color-error-60;
  }
}

.checkin-result__title--success {
  color: $color-success-100;
}

.checkin-result__title--warning {
  color: $color-warning-100;
}

.checkin-result__title--error {
  color: $color-text-error;
}

.checkin-result__message--error {
  color: $color-text-error;
}
</style>
